import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./SmoothScroll";
import { ease } from "./Reveal";

const links = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease }}
      className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6"
      data-testid="navbar"
    >
      <nav
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full border px-4 py-2.5 transition-[background-color,box-shadow,border-color] duration-500 sm:px-6 ${
          scrolled ? "border-white/70 bg-white/75 shadow-[0_10px_40px_-20px_rgba(11,79,138,0.45)] backdrop-blur-xl" : "border-transparent bg-white/30 backdrop-blur-md"
        }`}
      >
        <button onClick={() => go("top")} data-testid="nav-logo-button" aria-label="Back to top">
          <Logo />
        </button>
        <div className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="group relative rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute inset-x-4 bottom-1 h-px origin-left scale-x-0 bg-sky-600 transition-transform duration-500 group-hover:scale-x-100" />
            </button>
          ))}
        </div>
        <button
          onClick={() => go("contact")}
          data-testid="nav-cta-button"
          className="group hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-700 md:inline-flex"
        >
          Start an enquiry
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
        <button className="rounded-full p-2 text-ink md:hidden" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto mt-2 max-w-7xl rounded-3xl border border-white/70 bg-white/90 p-3 backdrop-blur-xl md:hidden"
          data-testid="nav-mobile-menu"
        >
          {[...links, { id: "contact", label: "Start an enquiry" }].map((l, i) => (
            <button key={i} onClick={() => go(l.id)} data-testid={`nav-mobile-link-${i}`} className="block w-full rounded-2xl px-4 py-3 text-left font-display text-lg text-ink hover:bg-sky-50">
              {l.label}
            </button>
          ))}
        </motion.div>
      )}
    </motion.header>
  );
};
